import React from "react";
import { Layout } from "antd";
import Header from "../../components/Header";
import FooterLayout from "../../components/Footer";
import { useNavigate } from "react-router-dom";
import { getAdvancedToolRoute } from "../../utils/navigation";
import "./HomePage.css";

const { Content } = Layout;

const TOOLS = [
  { icon: require("../../assets/icons/convert_word.svg").default,    title: "Word to PDF",       desc: "Convert .DOC & .DOCX files",         path: "/word2pdf",  color: "#2b5ce6" },
  { icon: require("../../assets/icons/convert_excel.svg").default,   title: "Excel to PDF",      desc: "Convert .XLS & .XLSX files",         path: "/excel2pdf", color: "#1a7a43" },
  { icon: require("../../assets/icons/convert_powerpoint.svg").default, title: "PowerPoint to PDF", desc: "Convert .PPT & .PPTX files",      path: "/ppt2pdf",   color: "#c84b1e" },
  { icon: require("../../assets/icons/convert_image.svg").default,   title: "Image to PDF",      desc: "JPG, PNG, WEBP & more",              path: "/image2pdf", color: "#7b3fe4" },
];

const STATS = [
  { num: "500K+",  label: "Files Converted" },
  { num: "120K+",  label: "QR Codes Created" },
  { num: "98%",    label: "Satisfaction Rate" },
  { num: "Free",   label: "Always & Forever" },
];

const ADVANCED_TOOLS = [
  { emoji: "🔗", title: "Merge PDF",        desc: "Join 2 or more PDF files into one",         color: "#6366f1" },
  { emoji: "✂️", title: "Split PDF",         desc: "Extract or separate pages from your PDF",   color: "#0891b2" },
  { emoji: "↕️", title: "Rearrange PDF",     desc: "Reorder pages in any order you want",       color: "#0891b2" },
  { emoji: "🗜️", title: "Compress PDF",      desc: "Reduce file size without losing quality",   color: "#1a7a43" },
  { emoji: "🔤", title: "OCR PDF",           desc: "Extract text from any scanned PDF",          color: "#7b3fe4" },
  { emoji: "✍️", title: "E-sign PDF",        desc: "Add your digital signature to any PDF",     color: "#c84b1e" },
  { emoji: "🔒", title: "Add Password",      desc: "Protect your PDF with a password",          color: "#e5322d" },
  { emoji: "🔓", title: "Remove Password",   desc: "Unlock a password-protected PDF",           color: "#e5322d" },
];

const QR_TOOLS = [
  { label: "Website", path: "/qr-link", icon: "↗", color: "#0891b2" },
  { label: "Text", path: "/qr-text", icon: "T", color: "#6366f1" },
  { label: "Location", path: "/qr-location", icon: "⌖", color: "#1a7a43" },
  { label: "Brand", path: "/qr-brand", icon: "B", color: "#e5322d" },
  { label: "Social", path: "/qr-social", icon: "@", color: "#7b3fe4" },
  { label: "File", path: "/qr-file", icon: "F", color: "#c84b1e" },
  { label: "vCard", path: "/qr-vcard", icon: "ID", color: "#2b5ce6" },
  { label: "Product", path: "/qr-product", icon: "#", color: "#d97706" },
  { label: "Message", path: "/qr-message", icon: "M", color: "#be185d" },
];

const TESTIMONIALS = [
  {
    name: "Vijay Mor",
    meta: "CSC operator",
    quote:
      "Really like the user-friendly interface. I run a CSE in a village where sending PDF files and converting Word or Excel files into PDF is a day-to-day task. The dedicated dashboard gives a VIP type feeling and makes users feel valued.",
  },
  {
    name: "Ayush",
    meta: "Bonoxi user",
    quote:
      "The website is so simple and self explanatory that it shows everything with clarity. Dusre websites me bhut confusion hozata tha jab open karte the, but here it is so easy to access whatever tool we want to use. Bonoxi is doing great for users.",
  },
  {
    name: "Dhruv",
    meta: "Chartered Accountant",
    quote:
      "I am a CA by profession and really liked all the tools Bonoxi is providing for free. I would like to request a PDF editing tool with precision editing so it can add more value in our personal and professional life. Thanks Bonoxi.",
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const isAuthenticated = !!localStorage.getItem("token");

  const goAdvancedTool = (toolTitle) => {
    navigate(
      isAuthenticated ? getAdvancedToolRoute(toolTitle) : "/login",
      isAuthenticated ? undefined : { state: { toolName: toolTitle } }
    );
  };

  return (
    <Layout style={{ backgroundColor: "#fff" }}>
      <Header />
      <Content>

        {/* ── Hero ── */}
        <section className="hp-hero">
          <div className="hp-hero-inner">
            <div className="hp-hero-text">
              <div className="hp-eyebrow">Free Online Tools</div>
              <h1 className="hp-hero-title">
                Simplify Your<br />
                <span className="hp-hero-accent">PDF & QR Tasks</span>
              </h1>
              <p className="hp-hero-desc">
                Convert Word, Excel, PowerPoint and images to PDF. Generate QR codes
                for links and text. Fast, free, and no sign-up needed for basic tools.
              </p>
              <div className="hp-hero-actions">
                <button
                  className="hp-btn-primary"
                  onClick={() => navigate(isAuthenticated ? "/dashboard/home" : "/register")}
                >
                  Get started free
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </button>
                <button className="hp-btn-ghost" onClick={() => navigate("/word2pdf")}>
                  Try Word to PDF
                </button>
              </div>
              <div className="hp-stats">
                {STATS.map((s) => (
                  <div key={s.label} className="hp-stat">
                    <span className="hp-stat-num">{s.num}</span>
                    <span className="hp-stat-label">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="hp-hero-visual">
              <div className="hp-hero-img-wrap">
                <img src={require("../../assets/home/hero-graphic.png")} alt="Bonoxi PDF Tools" />
              </div>
            </div>
          </div>
        </section>

        {/* ── Tools ── */}
        <section className="hp-tools">
          <div className="hp-section-inner">
            <div className="hp-section-head">
              <div className="hp-section-eyebrow">PDF Converter</div>
              <h2 className="hp-section-title">Convert any file to PDF</h2>
              <p className="hp-section-sub">No installation needed. Works right in your browser.</p>
            </div>
            <div className="hp-tools-grid">
              {TOOLS.map((tool) => (
                <div key={tool.path} className="hp-tool-card" onClick={() => navigate(tool.path)}>
                  <div className="hp-tool-icon-wrap" style={{ background: tool.color + "12" }}>
                    <img src={tool.icon} alt={tool.title} className="hp-tool-icon" />
                  </div>
                  <div className="hp-tool-body">
                    <div className="hp-tool-title">{tool.title}</div>
                    <div className="hp-tool-desc">{tool.desc}</div>
                  </div>
                  <div className="hp-tool-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Advanced PDF Tools ── */}
        <section className="hp-advanced">
          <div className="hp-section-inner">
            <div className="hp-section-head">
              <div className="hp-section-eyebrow">All PDF Tools</div>
              <h2 className="hp-section-title">More powerful PDF tools</h2>
              <p className="hp-section-sub">
                Merge, split, compress, sign and secure your PDFs.{" "}
                <span className="hp-adv-login-note">Free account required.</span>
              </p>
            </div>
            <div className="hp-adv-grid">
              {ADVANCED_TOOLS.map((tool) => (
                <div
                  key={tool.title}
                  className="hp-adv-card"
                  onClick={() => goAdvancedTool(tool.title)}
                >
                  <div className="hp-adv-emoji-wrap" style={{ background: tool.color + "10" }}>
                    <span className="hp-adv-emoji">{tool.emoji}</span>
                  </div>
                  <div className="hp-adv-body">
                    <div className="hp-adv-title">{tool.title}</div>
                    <div className="hp-adv-desc">{tool.desc}</div>
                  </div>
                  <div className="hp-adv-lock">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  </div>
                </div>
              ))}
            </div>
            <div className="hp-adv-cta">
              <button
                className="hp-adv-signup-btn"
                onClick={() => navigate(isAuthenticated ? "/dashboard/home" : "/register")}
              >
                Create free account to unlock all tools
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>
            </div>
          </div>
        </section>

        {/* ── QR section ── */}
        <section className="hp-qr">
          <div className="hp-section-inner hp-qr-inner">
            <div className="hp-qr-visual">
              <img src={require("../../assets/home/qr-graphic.png")} alt="QR Code" />
            </div>
            <div className="hp-qr-text">
              <div className="hp-section-eyebrow">QR Generator</div>
              <h2 className="hp-section-title">Create QR codes<br />in seconds</h2>
              <p className="hp-section-sub">
                Generate scannable QR codes for websites, text, locations, brands,
                social profiles, files, contacts, products and messages.
              </p>
              <div className="hp-qr-tool-grid">
                {QR_TOOLS.map((tool) => (
                  <button key={tool.path} className="hp-qr-tool-card" onClick={() => navigate(tool.path)}>
                    <span className="hp-qr-tool-icon" style={{ background: tool.color + "12", color: tool.color }}>{tool.icon}</span>
                    <span>{tool.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Why Bonoxi ── */}
        <section className="hp-why">
          <div className="hp-section-inner">
            <div className="hp-section-head">
              <div className="hp-section-eyebrow">Why Bonoxi</div>
              <h2 className="hp-section-title">Everything you need,<br />nothing you don't</h2>
            </div>
            <div className="hp-why-grid">
              {[
                { icon: "⚡", title: "Lightning Fast",    desc: "Files processed in seconds. No waiting in queues." },
                { icon: "🔒", title: "Secure & Private",  desc: "Files are deleted after conversion. Your data is yours." },
                { icon: "🆓", title: "Free to Use",       desc: "Core tools are always free. No hidden fees." },
                { icon: "📱", title: "Works Everywhere",  desc: "Browser-based. Works on any device, no install needed." },
                { icon: "☁️", title: "Cloud Storage",     desc: "Sign in to save and access your files anytime." },
                { icon: "📖", title: "Helpful Blog",      desc: "Guides and tips to help you work smarter." },
              ].map((f) => (
                <div key={f.title} className="hp-why-card">
                  <div className="hp-why-icon">{f.icon}</div>
                  <div className="hp-why-title">{f.title}</div>
                  <div className="hp-why-desc">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Testimonials ── */}
        <section className="hp-testimonials">
          <div className="hp-section-inner">
            <div className="hp-section-head">
              <div className="hp-section-eyebrow">Testimonials</div>
              <h2 className="hp-section-title">Loved by Bonoxi users</h2>
              <p className="hp-section-sub">
                Real feedback from users who rely on Bonoxi for daily document work.
              </p>
            </div>
            <div className="hp-testimonial-grid">
              {TESTIMONIALS.map((item) => (
                <article key={item.name} className="hp-testimonial-card">
                  <div className="hp-testimonial-mark">“</div>
                  <p className="hp-testimonial-quote">{item.quote}</p>
                  <div className="hp-testimonial-author">
                    <div className="hp-testimonial-avatar">{item.name.charAt(0)}</div>
                    <div>
                      <div className="hp-testimonial-name">{item.name}</div>
                      <div className="hp-testimonial-meta">{item.meta}</div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Mobile App ── */}
        <section className="hp-app">
          <div className="hp-section-inner hp-app-inner">
            <div className="hp-app-text">
              <div className="hp-section-eyebrow">Mobile App</div>
              <h2 className="hp-section-title">Take Bonoxi<br />wherever you go</h2>
              <p className="hp-section-sub">
                Convert files and generate QR codes straight from your phone.
                Available on Android — App Store coming soon.
              </p>
              <div className="hp-app-badges">
                <img
                  src={require("../../assets/home/google-play.png")}
                  alt="Google Play"
                  className="hp-app-badge"
                  onClick={() => window.open("https://play.google.com/store/apps/details?id=com.appsinfinito.love2pdf", "_blank")}
                />
                <img
                  src={require("../../assets/home/app-store.png")}
                  alt="App Store"
                  className="hp-app-badge hp-app-badge-muted"
                />
              </div>
            </div>
            <div className="hp-app-visual">
              <img src={require("../../assets/home/app-graphic.png")} alt="Bonoxi App" />
            </div>
          </div>
        </section>

        {/* ── CTA Banner ── */}
        <section className="hp-cta">
          <div className="hp-cta-inner">
            <h2 className="hp-cta-title">Ready to convert your first file?</h2>
            <p className="hp-cta-sub">Free forever for basic use. Sign up to save your history.</p>
            <div className="hp-cta-actions">
              <button className="hp-cta-btn" onClick={() => navigate("/word2pdf")}>Start converting</button>
              <button
                className="hp-cta-btn hp-cta-btn-outline"
                onClick={() => navigate(isAuthenticated ? "/dashboard/home" : "/register")}
              >
                {isAuthenticated ? "Open dashboard" : "Create free account"}
              </button>
            </div>
          </div>
        </section>

      </Content>
      <FooterLayout />
    </Layout>
  );
};

export default HomePage;
